import { useForm } from 'react-hook-form'
import { FormObjectType, SelfRegisterCouponFormItemsType } from 'src/types/react-hook-form'
import self_reg_coupon_default_image from 'src/assets/images/self_reg/self_reg_coupon_default_image.png'
import { ERRCODE_SUCCESS } from 'src/service/constant/errcodes'
import { UseMutateAsyncFunction } from '@tanstack/react-query'
import {
  ResponseDataType,
  SelfRegisterCouponImageDeleteSpace,
  SelfRegisterCouponImageUploadSpace,
} from 'src/types/api'
import { ChangeEvent } from 'react'
import { NumReplace, regExps } from 'src/utils/regExps_util'
import { useToast } from 'src/provider/ToastContextProvider'
import { useDelayTrackingMonitor } from 'src/utils/sentry/useDelayTrackingMonitor'

type useSelfRegisterFormParameterType = {
  selfImgUploadMutate: UseMutateAsyncFunction<
    ResponseDataType<SelfRegisterCouponImageUploadSpace.Response>,
    Error,
    any,
    unknown
  >
  selfImgDeleteMutate: UseMutateAsyncFunction<
    ResponseDataType<SelfRegisterCouponImageDeleteSpace.Response>,
    Error,
    any,
    unknown
  >
}

const useSelfRegisterForm = ({
  selfImgDeleteMutate,
  selfImgUploadMutate,
}: useSelfRegisterFormParameterType) => {
  const methods = useForm<SelfRegisterCouponFormItemsType>({
    mode: 'onBlur',
    defaultValues: {
      barcode: '',
      brandName: '',
      memo: '',
      couponImage: self_reg_coupon_default_image,
      couponName: '',
      validDate: '',
      useFlag: false,
    },
  })
  const { setValue, getValues, trigger } = methods
  const { openToast } = useToast()

  //web
  const couponImageOnchange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    const maxSize = 5 * 1024 * 1024
    const acceptFileType = ['.jpg', '.png', '.bmp', '.gif', '.jpeg', '.tif', '.tiff', '.svg']

    if (!acceptFileType.some((type) => file.name.includes(type))) {
      event.target.value = ''
      openToast({ contents: '이미지 파일만 업로드 가능합니다.' })
      return
    } else if (file.size > maxSize) {
      event.target.value = ''
      openToast({ contents: '5MB 이하 파일만 업로드 가능합니다.' })
      return
    } else {
      const reader = new FileReader()
      reader.onloadend = () => {
        selfImgUploadMutate({ CouponImage: reader.result as string }).then(({ Header, Body }) => {
          const { CouponImageURL, ExpireDT, Barcode } = Body!
          selfImgDeleteMutate({ CouponImageURL: getValues('couponImage') }).then(() => {
            if (Header.ErrCode === ERRCODE_SUCCESS) {
              setValue('barcode', Barcode)
              setValue('validDate', ExpireDT)
              setValue('couponImage', CouponImageURL)
              trigger?.()
            } else {
            }
          })
        })
      }
      reader.readAsDataURL(file)
    }
  }
  const barcodeOnchange = (event: { target: { value: string } }) => {
    setValue('barcode', NumReplace(event.target.value, 20), { shouldValidate: true })
  }
  const validDateOnchange = (event: { target: { value: string } }) => {
    setValue('validDate', event.target.value, { shouldValidate: true })
  }
  const couponNameOnchange = (event: { target: { value: string } }) => {
    setValue('couponName', event.target.value.slice(0, 100), { shouldValidate: true })
  }
  const brandNameOnchange = (event: { target: { value: string } }) => {
    setValue('brandName', event.target.value.slice(0, 100), { shouldValidate: true })
  }
  const memoOnchange = (event: { target: { value: string } }) => {
    setValue('memo', event.target.value.slice(0, 100), { shouldValidate: true })
  }

  const registerAttribute: Record<
    keyof SelfRegisterCouponFormItemsType,
    Partial<FormObjectType>
  > = {
    useFlag: {
      tag: 'useFlag',
      register_options: {
        required: false,
      },
    },
    couponImage: {
      tag: 'couponImage',
      register_options: {
        onChange: couponImageOnchange,
      },
    },
    barcode: {
      tag: 'barcode',
      placeholder: '바코드번호 입력',
      type: 'tel',
      register_options: {
        required: '바코드 번호를 입력해주세요.',
        pattern: { value: regExps.numReg.pattern, message: '숫자를 입력해주세요.' },
        onChange: barcodeOnchange,
        inputmode: 'numeric',
      },
    },
    validDate: {
      tag: 'validDate',
      placeholder: '사용가능한 유효기간 입력',
      type: 'date',
      register_options: {
        required: '유효기간을 입력해주세요.',
        onChange: validDateOnchange,
      },
    },
    couponName: {
      tag: 'couponName',
      placeholder: '쿠폰명 입력',
      type: 'text',
      register_options: {
        required: '쿠폰명을 입력해주세요.',
        pattern: { value: regExps.selfCouponName.pattern, message: '한글/영문을 입력해주세요' },
        onChange: couponNameOnchange,
      },
    },
    brandName: {
      tag: 'brandName',
      placeholder: '브랜드/사용처 입력',
      type: 'text',
      register_options: {
        onChange: brandNameOnchange,
      },
    },
    memo: {
      tag: 'memo',
      type: 'text',
      register_options: {
        onChange: memoOnchange,
      },
    },
  }
  return {
    methods,
    registerAttribute,
  }
}
export default useSelfRegisterForm
